<template>
  <div class="page page-terms-of-service">
    <TermsOfServiceMenu />
    <h1 class="title">
      Peatix Privacy Policy
    </h1>
    <p>
      Peatix Inc. and its affiliates (collectively “Peatix,” “we,” “us,” or “our”) respect the importance of personal information. To help ensure the protection of your personal information, we have established this Privacy Policy (“Privacy Policy”). Unless otherwise defined, definitions of terms used in this Privacy Policy are the same as those defined in the <RouterLink
        :to="ROUTE.TERMS.NAME"
        target="_blank"
      >
        Peatix Terms of Service
      </RouterLink>.
    </p>
    <article>
      <h2 class="article-title">
        1. Collection of Personal Information, etc.
      </h2>
      <p>
        We obtain your personal information in the following cases.
      </p>
      <section>
        <h3 class="section-title">
          1.1. Personal information required to provide our Services
        </h3>
        <p>We will obtain personal information as prescribed below from you to facilitate the use of our Services. If such information is not provided, you may not be able to use all or part of our Services.</p>
        <ol class="ordered-list">
          <li>
            <h4 class="topic">
              Information to register when using ticketing services
            </h4>
            <p>We will obtain the following information (“Member Information”) from you through the membership registration process.</p>
            <ul>
              <li>Nickname;</li>
              <li>Name;</li>
              <li>Email address;</li>
              <li>Profile image; and;</li>
              <li>Country of residence.</li>
            </ul>
            <p>If you agree to link your ID through a third-party service designated by us, we may obtain your personal information including the above information registered by you with the third party through such third-party service.</p>
          </li>
          <li>
            <h4 class="topic">
              Information obtained from settlement institutions
            </h4>
            <p>We will acquire certain information provided by you to our partner payment institutions to the extent necessary to facilitate our payment operations to settle payments for event tickets and other Services purchased by you and prevent unauthorized use of credit cards, etc. The handling of personal information by our partner settlement institutions is subject to the terms and policies established by each settlement institution. We do not store any of your payment information.</p>
          </li>
          <li>
            <h4 class="topic">
              Information obtained when you host events as an organizer
            </h4>
            <p>When you host an event as an organizer, we obtain the following information in addition to your Member Information.</p>
            <ul>
              <li>Phone number; and</li>
              <li>Bank account information.</li>
            </ul>
            <p>In addition to the above, we may obtain information including personal information such as information of event organizers and speakers, and photographs that you post on the event page.</p>
          </li>
          <li>
            <h4 class="topic">
              Additional Information we obtain for Token-gating Service
            </h4>
            <div />
            <ul>
              <li>Wallet ID</li>
            </ul>
          </li>
          <li>
            <h4 class="topic">
              Information obtained for safe use of Peatix
            </h4>
            <p>In order for you to safely use the Service, we will contact you at the designated contact point if any act is found to be in violation of our Terms of Use, etc. In connection with such communications, we may be provided with your user name, the name and URL of the Event you attended or hosted, details of acts suspected of violating our Terms of Service, photos and videos of Events you attended or hosted, etc. Personal information contained in such information is obtained for the safe operation of the Service by checking for violations of our Terms of Service, etc.</p>
          </li>
        </ol>
      </section>
      <section>
        <h3 class="section-title">
          1.2. Personal information provided with your consent
        </h3>
        <p>With your consent, we obtain a variety of information, including, but not limited to, your name, email address, address, phone number, company name, and job title.</p>
      </section>
      <section>
        <h3 class="section-title">
          1.3. Information provided by applicants for recruitment and internship
        </h3>
        <p>We obtain personal information of applicants for recruitment and internship through application, resume, interview, reference check, etc. We will not handle personal information beyond the scope of the purpose of use without the consent of the applicant.</p>
      </section>
      <section>
        <h3 class="section-title">
          1.4. Information needed to respond to your request
        </h3>
        <p>We will obtain personal information such as contact information including, but not limited to, name, email address and phone number necessary to respond to your request. We may also request information necessary to identify, investigate, and resolve the problem, including details of the event that is troubling you.</p>
      </section>
      <section>
        <h3 class="section-title">
          1.5. Information automatically obtained when using the Service
        </h3>
        <ol class="ordered-list">
          <li>
            <h4 class="topic">
              Usage status of the Service
            </h4>
            <p>We automatically obtain when and how you use our Service. The information which we collect includes, the date and time of your usage of our Service, the length of time you spend with our Service, the search results you use within our Service, and your usage status of content and ads you browse and tap.</p>
          </li>
          <li>
            <h4 class="topic">
              Application, browser, device, and network information
            </h4>
            <p>We obtain information about your device, browser, and apps (including, identifier for identifying the device/browser, cookies, OS, language and time zone settings, location information, browser type, application version, etc.), as well as network information (such as IP address). We may associate the acquired usage information with an internal identifier.</p>
          </li>
        </ol>
      </section>
      <section>
        <h3 class="section-title">
          1.6. Personal information obtained from third parties
        </h3>
        <p>We may obtain personal information from our business partners upon confirming that our business partners have obtained consent from the individuals to which the personal information belongs, and how the consent was obtained.</p>
      </section>
      <section>
        <h3 class="section-title">
          1.7. Personal information obtained from employees, etc.
        </h3>
        <p>We will acquire the following personal information from our employees and contractors who are engaged in our business through outsourcing. The information we obtain shall be limited to those required by applicable laws and regulations or necessary for our business.</p>
        <ul>
          <li>Basic information (name, address, date of birth, etc.);</li>
          <li>Internally disclosed information (user id, account name, department, job title, etc.);</li>
          <li>Compensation information (annual compensation, bonuses, etc.);</li>
          <li>Human resource information (performance evaluation, disciplinary action, commendation, etc.);</li>
          <li>Family and relative information (family structure, dependents, etc.); and</li>
          <li>Physical and health information (results of medical examinations, etc.).</li>
        </ul>
      </section>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        2. Purpose of Use of Personal Information
      </h2>
      <p>
        We use your information, including your Member Information, for the following purposes.
      </p>
      <section>
        <h3 class="section-title">
          2.1. To provide the Services safely and securely
        </h3>
        <p>Member Information and other information registered to use the Services will be used to provide the Services safely and securely for the following purposes.</p>
        <ul>
          <li>To appropriately implement and administer membership registration;</li>
          <li>To appropriately implement and administer event management and ticket sales;</li>
          <li>To properly implement and administer ticket purchases; and</li>
          <li>To inform you about changes to the Agreements, additional new functions and other important information required to use the Services.</li>
        </ul>
      </section>
      <section>
        <h3 class="section-title">
          2.2. To provide information optimized for you
        </h3>
        <p>To provide you with personalized information, we may use your information to display and to distribute email notifications on recommended events, new materials and information relating to special services, including relevant ads based on your event participation and search history.</p>
      </section>
      <section>
        <h3 class="section-title">
          2.3. To distribute direct email
        </h3>
        <p>For those who opt in to receive direct emails, we will use your information to deliver information that we think is useful to you. You can also opt out from our direct email service.</p>
      </section>
      <section>
        <h3 class="section-title">
          2.4. To improve the Services and develop new services
        </h3>
        <p>We use your information to measure the effectiveness of content posted and Event information emails distributed, conduct surveys on usage status and usage environment, and use it to develop new services.</p>
      </section>
      <section>
        <h3 class="section-title">
          2.5. To ensure security and prevent unauthorized use
        </h3>
        <p>We use your information to ensure security and to prevent unauthorized use of the Services to try to maintain a safe environment.</p>
        <ul>
          <li>To check for unauthorized use including violation of the Agreements, illegal activities, and unauthorized access;</li>
          <li>To prevent unauthorized use by monitoring, checking, and analyzing the content of violation reports and illegal events; and</li>
          <li>To confirm the facts and contact the person in question upon an occurrence, or the possibility of an occurrence, of unauthorized use.</li>
        </ul>
      </section>
      <section>
        <h3 class="section-title">
          2.6. To select applicants for recruitment and internship
        </h3>
        <p>We will use the information you provide to us upon your application for employment or internship for the following purposes.</p>
        <ul>
          <li>To determine whether applicants match the character, skills, experience, etc. that we are seeking when screening documents and conducting interviews at the time of employment selection;</li>
          <li>To contact applicants for the purpose of scheduling interviews, etc.;</li>
          <li>To verify past applications with us; and</li>
          <li>To use as a reference when formulating future hiring policies.</li>
        </ul>
      </section>
      <section>
        <h3 class="section-title">
          2.7. To contact you as needed
        </h3>
        <p>We will use your information to respond to your inquiries and to contact you if necessary.</p>
      </section>
      <section>
        <h3 class="section-title">
          2.8. To carry out necessary procedures for employees, etc.
        </h3>
        <p>We use personal information of our employees for the following purposes. Whenever we use personal information for purposes other than those listed below, we will clarify the purpose of use and obtain prior consent from the individual concerned. The personal information of retired employees will be used to prepare various personnel data and communication after retirement.</p>
        <ul>
          <li>Basic information (for employment management);</li>
          <li>Internally disclosed information (for business communication);</li>
          <li>Compensation information (for determining and paying salaries, withholding tax procedures, social insurance procedures, etc.);</li>
          <li>Human resource information (for determining assignments and duties, training and skill development, etc.);</li>
          <li>Family and relative information (for insurance related procedures, benefits, etc.); and</li>
          <li>Physical and health information (for ensuring a proper working environment, etc.).</li>
        </ul>
      </section>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        3. Provision of Personal Information to Third Parties
      </h2>
      <p>
        We may provide your personal information to third parties under any of the following circumstances.
      </p>
      <section>
        <h3 class="section-title">
          3.1. Where permitted by applicable law
        </h3>
        <ul>
          <li>When it is permitted by laws and regulations;</li>
          <li>When it is necessary to protect the life, body or property of an individual, and it is difficult to obtain consent from the person concerned;</li>
          <li>Cases in which the provision of personal information is particularly necessary for improving public health or promoting the sound growth of children and in which it is difficult to obtain the consent of the person concerned; or</li>
          <li>Cases in which the provision of personal information is necessary for cooperating with a state organization, a local government, or an individual or entity entrusted by either a state organization or a local government to execute affairs prescribed by law, and in which obtaining the consent of the person is likely to impede the execution of the affairs concerned.</li>
        </ul>
      </section>
      <section>
        <h3 class="section-title">
          3.2. When providing to a third party with the consent from you, or when you provide directly to a third party
        </h3>
        <ol class="ordered-list">
          <li>
            <h4 class="topic">
              Information provided to Organizers
            </h4>
            <p>Information provided to Organizers.</p>
            <ul>
              <li>
                <p>Member Information required to participate in the Event</p>
                <p>Among the Member Information obtained by us, names will be provided to Organizers in order to provide the information and Services you require. Among the Member Information, e-mail addresses will be provided to Organizers only if it is requested by Organizers and approved by us. Organizers who use the Services shall handle personal information in accordance with the Agreements.</p>
              </li>
              <li>
                <p>Information entered in Organizer questionnaires </p>
                <p>The Organizer may ask you to provide address, phone number, company name, job title and other personal information that the Organizer deems necessary ("Participant Information Required by Organizers") directly to the Organizer through a questionnaire for participants created on the Event page. We do not recognize the content of the Participant Information Required by Organizers and will not use it for our business. Upon using the questionnaire, please understand that the Organizer is responsible for managing the Participant Information Required by Organizers. The handling of personal information by the Organizer is subject to the rules and policies set by each Organizer.</p>
              </li>
            </ul>
          </li>
        </ol>
      </section>
      <section>
        <h3 class="section-title">
          3.3. Information provided to the third-party contractors
        </h3>
        <p>In order to provide the Services smoothly, the personal information obtained may be provided to third-party contractors selected in accordance with our security standards, and some operations necessary to provide the Services may be outsourced to third-party contractors. However, the personal information to which handling is outsourced shall be limited to the minimum information necessary for performing the outsourced operation.</p>
      </section>
      <section>
        <h3 class="section-title">
          3.4. Shared use among group companies
        </h3>
        <p>We may share the personal information described in "1. Collection of Personal Information" among the group companies listed in 10.1 below. The purpose of shared use is the same as described in " 2. Purpose of Use of Personal Information". Our group companies are in the United States, Japan, Singapore, and Malaysia.</p>
      </section>
      <section>
        <h3 class="section-title">
          3.5. Business Succession
        </h3>
        <p>In the event of an acquisition, merger, or change of business entity, or other inter-company transaction involving the Services and/or the Site, subject to this Privacy Policy, personal information may be transferred accordingly to the successor of the business, to the extent permitted by applicable laws.</p>
      </section>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        4. Use of Cookies and Analytic Tools
      </h2>
      <p>
        For our use of Cookies and Analytic Tools, please see our <RouterLink :to="ROUTE.COOKIE_POLICY.NAME">
          Cookie Policy
        </RouterLink>.
      </p>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        5. Security Control Measures
      </h2>
      <section>
        <h3 class="section-title">
          5.1. We will take the following security control measures for the management of personal information we have obtained.
        </h3>
        <p>We will obtain personal information as prescribed below from you to facilitate the use of our Services. If such information is not provided, you may not be able to use all or part of our Services.</p>
        <ul>
          <li>We will periodically check the status of compliance with internal rules related to the protection of personal information through audits.</li>
          <li>We will regularly provide training on the protection of personal information and try to ensure that all employees whose work involves personal data take measures to maintain confidentiality.</li>
          <li>We will restrict access to our offices and other facilities.</li>
          <li>We will have access control and security monitoring procedures in place to prevent unauthorized access, disclosure, unauthorized use, alteration, or destruction of personal information.</li>
        </ul>
      </section>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        6. Retention Period
      </h2>
      <section>
        <h3 class="section-title">
          6.1. Member Information
        </h3>
        <p>Generally, we will retain your Member Information in accordance with the terms of use described in this Privacy Policy to provide you the Services, unless you request deletion of your account. If you request us to delete your account, we will delete your information in accordance with applicable laws and internal rules after a certain period for the following reasons. Please note that we may retain a part of your information in a form that does not allow us to identify you after we receive your request to delete your account.</p>
        <ul>
          <li>For response to inquiries;</li>
          <li>For dispute settlement and collection and payment of event participation fees; and</li>
          <li>For detection of fraud and countermeasures.</li>
        </ul>
      </section>
      <section>
        <h3 class="section-title">
          6.2. Questionnaire Information
        </h3>
        <p>We will permanently delete information you submitted in the Organizer questionnaire after 30 days from the end of the Event.</p>
      </section>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        7. Your Rights
      </h2>
      <section>
        <h3 class="section-title">
          7.1. Requests for Disclosure
        </h3>
        <p>You may, to the extent permitted by applicable law, request us to notify or disclose the purpose of use, correct, add, or delete the content, suspend use, or eliminate, or discontinue the provision to a third party (“Disclosure”) of the retained personal information we hold. If you wish to make a request for Disclosure, please contact our “Complaint and Consultation Desk” below for procedures.</p>
      </section>
      <section>
        <h3 class="section-title">
          7.2. Complaint and Consultation Desk
        </h3>
        <p>For any inquiries regarding the use of your personal information, you may contact the "Complaint and Consultation Desk" below. After confirming your identity, we will respond to your inquiry within a reasonable time in accordance with the applicable law.</p>
      </section>
      <section>
        <div>
          <p>{{ `<Complaint and Consultation Desk>` }}</p>
          <p>Operations Manager</p>
          <p>Peatix Japan K.K.</p>
          <p>Shibuya Konno, 8th floor</p>
          <p>3-3-1 Shibuya,Shibuya-ku, Tokyo 150-0002, Japan</p>
        </div>
        <div>
          <p>
            <a
              href="https://us-help-attendee.peatix.com/support/tickets/new"
              target="_blank"
            >Inquiry Form</a>
          </p>
          <p>(We will reply during 10: 00 ~ 18: 00 on weekdays.)</p>
        </div>
      </section>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        8. Privacy of Minors
      </h2>
      <p>If you are a minor, you should not provide personal information through the Services without the consent of a parent or guardian. You represent and warrant that you have the rights and competencies necessary to use the Services or, if you are a minor, that you use the Services with the consent of a parent or guardian.</p>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        9. Changes to this Policy
      </h2>
      <div>
        <p>This policy is subject to change from time to time, and the latest Privacy Policy will be published on the Site.</p>
        <p>Your continued use of the Services after notice or publication of any changes shall constitute acceptance of the changes and agreement to this Privacy Policy, as amended.</p>
        <p>Continuing to use the Services after a change has been notified or published on the Site constitutes acceptance of the change and acceptance of the amended Privacy Policy.</p>
      </div>
      <Divider />
    </article>
    <article>
      <h2 class="article-title">
        10. Others
      </h2>
      <section>
        <h3 class="section-title">
          10.1. Name of business operators
        </h3>
        <div>
          <p>Peatix Inc. (United States)</p>
          <p>Peatix Japan K.K. (Japan)</p>
          <p>Peatix Asia Pte. Ltd. (Singapore)</p>
          <p>Peatix Malaysia Sdn. Bhd. (Malaysia)</p>
        </div>
      </section>
      <section>
        <h3 class="section-title">
          10.2. Business entity, title, and contact information of the personal information protection manager
        </h3>
        <div>
          <p>For the United States:</p>
          <p>Peatix Inc. IT Director</p>
        </div>
      </section>
      <Divider />
    </article>
    <article>
      <TermsLastUpdatedSection display-date="June 25, 2024" />
    </article>
    <GetInTouch />
  </div>
</template>
<style scoped lang="scss">
.section-title {
    @include fonts-get(regular, text-sm);
    color: colors-get(gray, 900);
}

ol.ordered-list {
  counter-reset: item;

  > li {
    list-style: none;

     &::before {
        content: "(" counter(item) ")";
        counter-increment: item;
        padding: 0 spacings-get(2);
     }

     > * {
        margin-bottom: spacings-get(4);
     }
  }

  .topic {
    display: inline;
    @include fonts-get(regular, text-sm);
  }
}
</style>